import { DetectedBlob, Hole, ScoringEvent, Rectangle } from '../types';
import { BlobTracker } from './blobTracker';
import { DETECTION_CONFIG } from '../config/detectionConfig';

export class BallDetector {
  private previousFrame: ImageData | null = null;
  private trackedBalls: Map<number, TrackedBall> = new Map();
  private nextBallId: number = 0;
  private lastGlobalScore: number = 0;
  private previousBlobsInStartZone: Array<{ x: number; y: number }> = [];
  private lastBallCreationTime: number = 0;

  // Feature flag: Use centroid tracking for ball creation
  private readonly USE_CENTROID_TRACKING = DETECTION_CONFIG.USE_CENTROID_TRACKING;
  private blobTracker: BlobTracker | null = null;
  // Detection parameters (tunable based on testing)
  private readonly MOTION_THRESHOLD = 50; // Pixel difference threshold (increased to reduce noise)
  private readonly MIN_BLOB_AREA = 150; // Minimum blob size (reduced - ball might be partially occluded)
  private readonly MAX_BLOB_AREA = 5000; // Maximum blob size

  // Tracking parameters (tunable for fast-moving balls and occlusion)
  private readonly MIN_VISIBLE_FRAMES = 3; // Ball must be visible for this many frames total (reduced for fast balls)
  private readonly MIN_FRAMES_IN_HOLE = 1; // Ball must be in final hole for this many frames (reduced for fast/occluded balls)
  private readonly DISAPPEARANCE_FRAMES = 15; // Frames ball must be gone to confirm score (0.25 seconds at 60fps)
  private readonly MAX_TRACKING_DISTANCE = 400; // Max distance to associate blob with tracked ball (increased for fast balls and occlusion)

  // Cleanup and cooldown parameters
  private readonly BALL_TIMEOUT_MS = 3000; // Remove tracked ball after this time (increased - keep tracking during bounces)
  private readonly COOLDOWN_MS = 2000; // Cooldown after scoring (increased to prevent double-scoring)
  private readonly GLOBAL_COOLDOWN_MS = 1500; // Global scoring rate limit (increased significantly)

  // Tracking mode
  private readonly REQUIRE_HOLE_ENTRY = false; // Track all balls, not just those in holes (allows tracking before entry)

  constructor() {
    if (this.USE_CENTROID_TRACKING) {
      this.blobTracker = new BlobTracker();
    }
  }

  detect(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    holes: Hole[],
    startZone?: Rectangle,
    gutterZone?: Rectangle
  ): { blobs: DetectedBlob[]; events: ScoringEvent[] } {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { blobs: [], events: [] };

    // Draw current frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const currentFrame = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Skip first frame
    if (!this.previousFrame) {
      this.previousFrame = currentFrame;
      return { blobs: [], events: [] };
    }

    // Detect motion
    const motionMask = this.detectMotion(this.previousFrame, currentFrame);

    // Find blobs
    const blobs = this.findBlobs(motionMask, canvas.width, canvas.height);

    // Update tracked balls and check for scoring
    const events = this.updateTrackedBalls(blobs, holes, startZone, gutterZone);

    // Update previous frame
    this.previousFrame = currentFrame;

    return { blobs, events };
  }

  private detectMotion(prev: ImageData, curr: ImageData): boolean[][] {
    const width = prev.width;
    const height = prev.height;
    const mask: boolean[][] = Array(height)
      .fill(null)
      .map(() => Array(width).fill(false));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;

        // Calculate grayscale difference
        const prevGray = (prev.data[idx] + prev.data[idx + 1] + prev.data[idx + 2]) / 3;
        const currGray = (curr.data[idx] + curr.data[idx + 1] + curr.data[idx + 2]) / 3;

        const diff = Math.abs(prevGray - currGray);

        if (diff > this.MOTION_THRESHOLD) {
          mask[y][x] = true;
        }
      }
    }

    return mask;
  }

  private findBlobs(mask: boolean[][], width: number, height: number): DetectedBlob[] {
    const visited: boolean[][] = Array(height)
      .fill(null)
      .map(() => Array(width).fill(false));
    const blobs: DetectedBlob[] = [];

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (mask[y][x] && !visited[y][x]) {
          const blob = this.floodFill(mask, visited, x, y, width, height);
          if (blob.area >= this.MIN_BLOB_AREA && blob.area <= this.MAX_BLOB_AREA) {
            blobs.push(blob);
          }
        }
      }
    }

    return blobs;
  }

  private floodFill(
    mask: boolean[][],
    visited: boolean[][],
    startX: number,
    startY: number,
    width: number,
    height: number
  ): DetectedBlob {
    const stack: [number, number][] = [[startX, startY]];
    let sumX = 0;
    let sumY = 0;
    let count = 0;

    while (stack.length > 0) {
      const [x, y] = stack.pop()!;

      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      if (visited[y][x] || !mask[y][x]) continue;

      visited[y][x] = true;
      sumX += x;
      sumY += y;
      count++;

      // 4-connected neighbors
      stack.push([x + 1, y]);
      stack.push([x - 1, y]);
      stack.push([x, y + 1]);
      stack.push([x, y - 1]);
    }

    const centroidX = sumX / count;
    const centroidY = sumY / count;
    const radius = Math.sqrt(count / Math.PI);

    return {
      x: centroidX,
      y: centroidY,
      radius,
      area: count,
    };
  }

  private updateTrackedBalls(
    blobs: DetectedBlob[],
    holes: Hole[],
    startZone?: Rectangle,
    gutterZone?: Rectangle
  ): ScoringEvent[] {
    const events: ScoringEvent[] = [];
    const now = Date.now();
    const matchedBlobIndices = new Set<number>();
    const matchedBallIds = new Set<number>();

    // Associate existing tracked balls with current blobs
    this.associateBlobsWithBalls(blobs, holes, gutterZone, now, matchedBlobIndices, matchedBallIds);

    // Track current blobs in START zone for next frame
    const currentBlobsInStartZone: Array<{ x: number; y: number }> = [];

    // Identify blobs currently in START zone (don't create balls yet)
    blobs.forEach((blob, index) => {
      if (!matchedBlobIndices.has(index)) {
        const inStartZone = !startZone || this.isInRectangle(blob.x, blob.y, startZone);
        if (inStartZone) {
          currentBlobsInStartZone.push({ x: blob.x, y: blob.y });
        }
      }
    });

    // ========== BALL CREATION LOGIC ==========
    // Two modes: OLD (position-based) and NEW (centroid tracking)

    if (this.USE_CENTROID_TRACKING && this.blobTracker) {
      // ========== NEW: Centroid Tracking Approach ==========
      // Update blob tracker with current detections
      const blobsReadyForBallCreation = this.blobTracker.update(
        blobs.map(b => ({ x: b.x, y: b.y })),
        startZone,
        now
      );

      // Create balls for blobs that exited START zone
      for (const trackedBlob of blobsReadyForBallCreation) {
        // Limit to 9 balls (IDs 0-8)
        if (this.nextBallId >= 9) {
          console.log(`[BallDetector] ⛔ Max balls reached (9), ignoring new ball`);
          continue;
        }

        const ballId = this.nextBallId++;
        const currentHole = this.findHoleForPosition(trackedBlob.x, trackedBlob.y, holes);
        const inGutter = gutterZone ? this.isInRectangle(trackedBlob.x, trackedBlob.y, gutterZone) : false;

        this.trackedBalls.set(ballId, {
          id: ballId,
          lastX: trackedBlob.x,
          lastY: trackedBlob.y,
          lastSeen: now,
          createdAt: now,
          lastHole: currentHole,
          inGutter: inGutter,
          framesInFinalZone: 0,
          framesDisappeared: 0,
          state: 'ACTIVE',
          finalScore: -1,
        });

        // Mark blob as having created a ball
        this.blobTracker.markBallCreated(trackedBlob.id);

        console.log(`[Ball ${ballId}] 🎯 Started tracking at (${Math.round(trackedBlob.x)}, ${Math.round(trackedBlob.y)}) - EXITED START zone [CENTROID TRACKING - Blob ID: ${trackedBlob.id}]`);
      }
    } else {
      // ========== OLD: Position-Based Approach (with cooldown band-aid) ==========
      // Detect blobs that EXITED START zone (were there last frame, not there now)
      // Create at most ONE ball per frame to prevent duplicates from multiple blob detections
      const EXIT_DETECTION_DISTANCE = 150; // pixels - how far blob can move between frames
      const BALL_CREATION_COOLDOWN = 500; // milliseconds - minimum time between creating new balls (humans can't throw faster than this)

      let ballCreatedThisFrame = false;

      for (const prevBlob of this.previousBlobsInStartZone) {
      if (ballCreatedThisFrame) {
        break; // Only create one ball per frame
      }

      // Check if this previous blob is still in START zone
      const stillInStartZone = currentBlobsInStartZone.some(currentBlob => {
        const dx = currentBlob.x - prevBlob.x;
        const dy = currentBlob.y - prevBlob.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < EXIT_DETECTION_DISTANCE;
      });

      if (stillInStartZone) {
        continue; // Blob still in START zone - don't create ball yet
      }

      // Blob EXITED START zone! Find where it is now (current position)
      let matchingCurrentBlob: { x: number; y: number } | null = null;
      let minDistance = Infinity;

      blobs.forEach((blob, index) => {
        if (matchedBlobIndices.has(index)) return; // Skip already matched blobs

        const dx = blob.x - prevBlob.x;
        const dy = blob.y - prevBlob.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < EXIT_DETECTION_DISTANCE && distance < minDistance) {
          minDistance = distance;
          matchingCurrentBlob = { x: blob.x, y: blob.y };
        }
      });

      // If we can't find the blob in current frame, use the previous position
      // (blob might have moved very fast or temporarily not detected)
      if (!matchingCurrentBlob) {
        matchingCurrentBlob = { x: prevBlob.x, y: prevBlob.y };
      }

      // Check cooldown - prevent creating balls too quickly (duplicate prevention)
      const timeSinceLastBall = now - this.lastBallCreationTime;
      if (timeSinceLastBall < BALL_CREATION_COOLDOWN) {
        console.log(`[COOLDOWN] ⏱️ Blocked ball creation - only ${Math.round(timeSinceLastBall)}ms since last ball (need ${BALL_CREATION_COOLDOWN}ms)`);
        continue; // Too soon after last ball creation
      }

      // CREATE NEW BALL - blob just exited START zone!
      // Use CURRENT position (where ball is NOW), not previous position (where it WAS in START)
      const ballId = this.nextBallId++;
      const currentHole = this.findHoleForPosition(matchingCurrentBlob.x, matchingCurrentBlob.y, holes);
      const inGutter = gutterZone ? this.isInRectangle(matchingCurrentBlob.x, matchingCurrentBlob.y, gutterZone) : false;

      this.trackedBalls.set(ballId, {
        id: ballId,
        lastX: matchingCurrentBlob.x,
        lastY: matchingCurrentBlob.y,
        lastSeen: now,
        createdAt: now, // Track creation time for duplicate prevention
        lastHole: currentHole,
        inGutter: inGutter,
        framesInFinalZone: 0,
        framesDisappeared: 0,
        state: 'ACTIVE',
        finalScore: -1,
      });

      this.lastBallCreationTime = now; // Update cooldown timer
      ballCreatedThisFrame = true; // Prevent creating more balls this frame

      // Calculate time since last ball for logging
      const timeSincePreviousBall = this.trackedBalls.size > 0
        ? Math.min(...Array.from(this.trackedBalls.values()).map(b => now - b.createdAt))
        : 0;

      console.log(`[Ball ${ballId}] 🎯 Started tracking at (${Math.round(matchingCurrentBlob.x)}, ${Math.round(matchingCurrentBlob.y)}) - EXITED START zone [${timeSincePreviousBall > 0 ? `+${Math.round(timeSincePreviousBall)}ms since last ball` : 'first ball'}]`);

        // IMPORTANT: Clear previousBlobsInStartZone to prevent creating multiple balls from the same physical ball
        // The ball has exited, so we don't want to process any other stored positions from when it was in START zone
        this.previousBlobsInStartZone = [];
        break; // Exit the loop - we created a ball
      }

      // Update previous blobs in START zone for next frame (only if we didn't create a ball)
      if (!ballCreatedThisFrame) {
        this.previousBlobsInStartZone = currentBlobsInStartZone;
      }
    } // End of OLD approach
    // ========== END BALL CREATION LOGIC ==========

    // Check for balls that have disappeared (potential scores)
    for (const [ballId, ball] of this.trackedBalls.entries()) {
      // Skip balls that have already been scored
      if (ball.state === 'SCORED') {
        continue;
      }

      if (matchedBallIds.has(ballId)) {
        // Ball is still visible - reset disappearance counter
        ball.framesDisappeared = 0;
      } else {
        // Ball not seen this frame
        ball.framesDisappeared++;

        // Check if ball should score
        if (ball.state === 'IN_FINAL_ZONE' && ball.framesDisappeared === this.DISAPPEARANCE_FRAMES) {
          // SCORE! Ball was in final zone and has now disappeared
          const scoreValue = ball.finalScore;
          const zoneName = ball.inGutter ? 'GUTTER' : (ball.lastHole ? ball.lastHole.value + 'pt' : 'unknown');
          const ballLifetime = now - ball.createdAt;

          console.log(`[Ball ${ballId}] ✅ SCORED ${scoreValue} points! (zone: ${zoneName}, disappeared: ${ball.framesDisappeared}, lifetime: ${Math.round(ballLifetime)}ms)`);

          if (scoreValue > 0 && ball.lastHole) {
            events.push({
              id: `event-${now}-${ball.id}`,
              ballId: ball.id, // Include ball ID from detector
              holeId: ball.lastHole.id,
              holeValue: scoreValue,
              timestamp: now,
              confidence: 0.95,
              x: ball.lastX,
              y: ball.lastY,
            });
          } else if (scoreValue === 0) {
            // Gutter - log but don't add to events (or add with 0 points if you want to show it)
            console.log(`[Ball ${ballId}] 💀 Gutter (0 points)`);
          }

          ball.state = 'SCORED';

          // Remove scored ball immediately - it's gone into a hole and won't come back
          console.log(`[Ball ${ballId}] 🗑️ Removed (scored: ${scoreValue}pt)`);
          this.trackedBalls.delete(ballId);
        }
      }
    }

    // Cleanup old balls
    this.cleanupOldBalls(now);

    return events;
  }

  private associateBlobsWithBalls(
    blobs: DetectedBlob[],
    holes: Hole[],
    gutterZone: Rectangle | undefined,
    now: number,
    matchedBlobIndices: Set<number>,
    matchedBallIds: Set<number>
  ) {
    // For each tracked ball, find the closest blob
    for (const [ballId, ball] of this.trackedBalls.entries()) {
      if (ball.state === 'SCORED') continue;

      let closestBlobIndex = -1;
      let closestDistance = this.MAX_TRACKING_DISTANCE;

      blobs.forEach((blob, index) => {
        if (matchedBlobIndices.has(index)) return;

        const dx = blob.x - ball.lastX;
        const dy = blob.y - ball.lastY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestBlobIndex = index;
        }
      });

      if (closestBlobIndex >= 0) {
        const blob = blobs[closestBlobIndex];

        // Log large position jumps (possible mis-match of different balls)
        const dx = blob.x - ball.lastX;
        const dy = blob.y - ball.lastY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > 200) {
          console.log(`[Ball ${ballId}] ⚠️ LARGE JUMP: ${Math.round(distance)}px from (${Math.round(ball.lastX)}, ${Math.round(ball.lastY)}) to (${Math.round(blob.x)}, ${Math.round(blob.y)})`);
        }

        // Update position and zone
        const previousHole = ball.lastHole;
        const previousInGutter = ball.inGutter;

        const newHole = this.findHoleForPosition(blob.x, blob.y, holes);
        const newInGutter = gutterZone ? this.isInRectangle(blob.x, blob.y, gutterZone) : false;

        ball.lastX = blob.x;
        ball.lastY = blob.y;
        ball.lastSeen = now;
        ball.lastHole = newHole;
        ball.inGutter = newInGutter;

        // Check if ball entered a final zone (hole or gutter)
        const inFinalZone = newHole !== null || newInGutter;

        if (inFinalZone) {
          ball.state = 'IN_FINAL_ZONE';
          ball.framesInFinalZone++;
          ball.finalScore = newInGutter ? 0 : (newHole?.value ?? 0);

          // Log zone changes
          const zoneChanged = (previousHole?.id !== newHole?.id) || (previousInGutter !== newInGutter);
          if (zoneChanged) {
            const prevZoneName = previousInGutter ? 'GUTTER (0pt)' : (previousHole ? previousHole.value + 'pt' : 'none');
            const newZoneName = newInGutter ? 'GUTTER (0pt)' : (newHole ? newHole.value + 'pt' : 'none');
            console.log(`[Ball ${ballId}] 🔄 Zone: ${prevZoneName} → ${newZoneName} at (${Math.round(ball.lastX)}, ${Math.round(ball.lastY)})`);
          }
        } else {
          ball.state = 'ACTIVE';
          ball.framesInFinalZone = 0;
        }

        matchedBlobIndices.add(closestBlobIndex);
        matchedBallIds.add(ballId);
      }
    }
  }

  private findHoleForPosition(x: number, y: number, holes: Hole[]): Hole | null {
    const matchingHoles: Array<{ hole: Hole; distance: number }> = [];

    // Find all holes that contain this position
    for (const hole of holes) {
      const shape = hole.shape || 'circle'; // default to circle for backward compatibility
      let isInside = false;
      let distance = 0;

      if (shape === 'rectangle') {
        // Check if position is inside rectangle
        if (hole.width !== undefined && hole.height !== undefined) {
          isInside = x >= hole.x &&
                     x <= hole.x + hole.width &&
                     y >= hole.y &&
                     y <= hole.y + hole.height;

          // For rectangles, calculate distance to center for priority sorting
          const centerX = hole.x + hole.width / 2;
          const centerY = hole.y + hole.height / 2;
          const dx = x - centerX;
          const dy = y - centerY;
          distance = Math.sqrt(dx * dx + dy * dy);
        }
      } else {
        // Circle shape (default)
        const dx = x - hole.x;
        const dy = y - hole.y;
        distance = Math.sqrt(dx * dx + dy * dy);
        isInside = distance <= (hole.radius || 0);
      }

      if (isInside) {
        matchingHoles.push({ hole, distance });
      }
    }

    // If no holes match, return null
    if (matchingHoles.length === 0) {
      return null;
    }

    // If only one hole matches, return it
    if (matchingHoles.length === 1) {
      return matchingHoles[0].hole;
    }

    // Multiple holes overlap - prioritize by value (higher value wins)
    // If values are equal, choose the one with closest center
    matchingHoles.sort((a, b) => {
      // First, sort by value (descending - higher value first)
      if (a.hole.value !== b.hole.value) {
        return b.hole.value - a.hole.value;
      }
      // If values are equal, sort by distance (ascending - closer first)
      return a.distance - b.distance;
    });

    return matchingHoles[0].hole;
  }

  private isInRectangle(x: number, y: number, rect: Rectangle): boolean {
    return x >= rect.x &&
           x <= rect.x + rect.width &&
           y >= rect.y &&
           y <= rect.y + rect.height;
  }

  private cleanupOldBalls(now: number) {
    const ballsToRemove: number[] = [];

    for (const [ballId, ball] of this.trackedBalls.entries()) {
      // Remove balls that haven't been seen in a long time (timeout)
      // Note: Scored balls are removed immediately when they score, so we only check for timeout here
      if (ball.state !== 'SCORED' && now - ball.lastSeen > this.BALL_TIMEOUT_MS) {
        console.log(`[Ball ${ballId}] ⏱️ Removed (timeout - not seen for ${Math.round(now - ball.lastSeen)}ms)`);
        ballsToRemove.push(ballId);
      }
    }

    ballsToRemove.forEach(id => this.trackedBalls.delete(id));
  }

  reset() {
    this.previousFrame = null;
    this.trackedBalls.clear();
    this.nextBallId = 0;
    this.lastGlobalScore = 0;
    if (this.blobTracker) {
      this.blobTracker.reset();
    }
  }
}

type BallState = 'ACTIVE' | 'IN_FINAL_ZONE' | 'SCORED';

interface TrackedBall {
  id: number;
  lastX: number;
  lastY: number;
  lastSeen: number;
  createdAt: number;            // Timestamp when ball was created (for duplicate prevention)
  lastHole: Hole | null;
  inGutter: boolean;            // Is ball in gutter zone?
  framesInFinalZone: number;    // How many frames ball has been in final zone (hole or gutter)
  framesDisappeared: number;    // How many frames since ball was last seen
  state: BallState;             // Current state of ball tracking
  finalScore: number;           // Score to award when ball disappears
}

